import { z } from 'zod';
import {
  type CreateProjectInput,
  type ProjectId,
  ProjectSchema,
  type UpdateProjectInput,
} from '@voll-intelligence/domain';
import { PERMISSIONS } from '@voll-intelligence/voll-authz';
import type { HttpService } from '@web/data/services';

/**
 * Wire shape narrows the wire-side `permissions: string[]` to the
 * exhaustive `Permission` enum so a stale/typo'd key from the backend
 * fails loudly instead of silently denying. Domain keeps `permissions`
 * as `string[]` per its purity rule (no voll-authz dep); the narrowing
 * happens here at the read seam, and the narrowed type is what flows
 * to consumers via `ProjectWithPermissions` so `has(perm: Permission)`
 * stays type-safe end-to-end.
 */
const ProjectWithPermissionsSchema = ProjectSchema.extend({
  permissions: z.array(z.enum(PERMISSIONS)).default([]),
});
export type ProjectWithPermissions = z.infer<typeof ProjectWithPermissionsSchema>;

const ListProjectsResponseSchema = z.object({
  projects: z.array(ProjectWithPermissionsSchema),
});
const ProjectResponseSchema = z.object({ project: ProjectWithPermissionsSchema });

/**
 * Authz Fase 6 — all calls require an `orgId` and hit
 * `/orgs/:orgId/projects/*`. The orgId comes from `useOrgContext`
 * (see `ui/core/hooks/use_org_context.ts`) which derives it from the
 * URL slug via the org route loader.
 */
interface ProjectsRepository {
  loadProjects: (orgId: string) => Promise<ProjectWithPermissions[]>;
  getProject: (orgId: string, projectId: ProjectId) => Promise<ProjectWithPermissions>;
  createProject: (orgId: string, input: CreateProjectInput) => Promise<ProjectWithPermissions>;
  updateProject: (
    orgId: string,
    projectId: ProjectId,
    input: UpdateProjectInput,
  ) => Promise<ProjectWithPermissions>;
}

export function createProjectsRepository(httpService: HttpService): ProjectsRepository {
  async function loadProjects(orgId: string): Promise<ProjectWithPermissions[]> {
    const { projects } = await httpService.get(`/orgs/${orgId}/projects`, {
      schema: ListProjectsResponseSchema,
    });
    return projects;
  }

  async function getProject(orgId: string, projectId: ProjectId): Promise<ProjectWithPermissions> {
    const { project } = await httpService.get(`/orgs/${orgId}/projects/${projectId}`, {
      schema: ProjectResponseSchema,
    });
    return project;
  }

  async function createProject(
    orgId: string,
    input: CreateProjectInput,
  ): Promise<ProjectWithPermissions> {
    const { project } = await httpService.post(`/orgs/${orgId}/projects`, input, {
      schema: ProjectResponseSchema,
    });
    return project;
  }

  async function updateProject(
    orgId: string,
    projectId: ProjectId,
    input: UpdateProjectInput,
  ): Promise<ProjectWithPermissions> {
    const { project } = await httpService.patch(`/orgs/${orgId}/projects/${projectId}`, input, {
      schema: ProjectResponseSchema,
    });
    return project;
  }

  return { loadProjects, getProject, createProject, updateProject };
}
